<script lang="ts">
  // A declaration is a held relationship, not a one-shot flourish. This screen-space layer works for
  // the CSS field and the parked WebGL arena: it resolves both combatants to viewport coordinates,
  // keeps a live energy arc between them through response windows, and names both sides explicitly.
  import { onMount } from "svelte";
  import { duel, arenaZoneRect, mrefKey, type ScreenRect } from "../../stores/duel.svelte.ts";
  import { cardImage } from "../../../data.ts";

  let { nameOf }: { nameOf: (code: number) => string } = $props();

  type Point = { x: number; y: number };
  type Route = { start: Point; end: Point; control: Point; path: string };
  let route = $state<Route | null>(null);

  const d = $derived(duel.attackDeclaration);
  const attackerOwner = $derived(d ? (d.attacker.controller === duel.seat ? "You" : duel.oppName) : "");
  const defenderOwner = $derived(d ? (d.defenderController === duel.seat ? "You" : duel.oppName) : "");
  const attackerName = $derived(d ? (d.attackerCode ? nameOf(d.attackerCode) : "Attacking monster") : "");
  const defenderName = $derived(d ? (d.target ? (d.targetCode ? nameOf(d.targetCode) : "Set monster") : "Direct attack") : "");
  const status = $derived.by(() => {
    if (!d) return "";
    if (d.phase === "negated") return "Attack negated";
    if (d.phase === "striking") return "Attack resolving";
    if (duel.prompt?.kind === "selectChain") return "Response window";
    if (duel.oppThinking) return `${duel.oppName} responding`;
    return "Attack declared";
  });

  const fmt = (n: number | null): string => n === null || n === -2 ? "?" : n.toLocaleString();
  const center = (r: ScreenRect): Point => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });

  function rectFor(ref: { controller: number; location: number; sequence: number }): ScreenRect | null {
    const el = document.querySelector<HTMLElement>(`[data-mref="${mrefKey(ref.controller, ref.location, ref.sequence)}"]`);
    const b = el?.getBoundingClientRect();
    if (b?.width) return b;
    return arenaZoneRect(ref.controller, ref.location, ref.sequence);
  }

  /** Direct attacks still need a physical target. Aim at the middle card in the defender's actual
   *  hand fan; with an empty hand, aim at the centre of the hand zone rather than jumping to the LP
   *  HUD. This keeps declaration and impact on the same field-space axis. */
  function handPoint(controller: number): Point {
    const side = controller === duel.seat ? "me" : "opp";
    const row = document.querySelector<HTMLElement>(`[data-mref-hand="${side}"]`);
    const cards = row ? [...row.children].map((el) => el.getBoundingClientRect()).filter((b) => b.width > 0 && b.height > 0) : [];
    const middle = cards.length ? cards[Math.floor(cards.length / 2)] : null;
    if (middle) return { x: middle.left + middle.width / 2, y: middle.top + middle.height / 2 };
    const b = row?.getBoundingClientRect();
    if (b?.width) return { x: b.left + b.width / 2, y: b.top + b.height / 2 };
    return { x: innerWidth / 2, y: controller === duel.seat ? innerHeight * 0.86 : innerHeight * 0.14 };
  }

  function computeRoute(): Route | null {
    if (!d) return null;
    const a = rectFor(d.attacker);
    if (!a) return null;
    const start = center(a);
    const end = d.target ? (rectFor(d.target) ? center(rectFor(d.target)!) : null) : handPoint(d.defenderController);
    if (!end) return null;
    const distance = Math.hypot(end.x - start.x, end.y - start.y);
    // Bow upward like an attack trajectory, but cap it so the curve never collides with the top HUD.
    const lift = Math.min(112, Math.max(44, distance * 0.2));
    const control = { x: (start.x + end.x) / 2, y: Math.max(78, Math.min(start.y, end.y) - lift) };
    return { start, end, control, path: `M ${start.x} ${start.y} Q ${control.x} ${control.y} ${end.x} ${end.y}` };
  }

  function routeChanged(a: Route | null, b: Route | null): boolean {
    if (!a || !b) return a !== b;
    return Math.abs(a.start.x - b.start.x) > 0.5 || Math.abs(a.start.y - b.start.y) > 0.5 ||
      Math.abs(a.end.x - b.end.x) > 0.5 || Math.abs(a.end.y - b.end.y) > 0.5;
  }

  onMount(() => {
    let frame = 0;
    let alive = true;
    const update = () => {
      if (!alive) return;
      const next = computeRoute();
      if (routeChanged(route, next)) route = next;
      frame = requestAnimationFrame(update);
    };
    update();
    return () => { alive = false; cancelAnimationFrame(frame); };
  });
</script>

{#if d}
  <div class="attack-declaration pointer-events-none fixed inset-0 z-[47]" class:is-striking={d.phase === "striking"} class:is-negated={d.phase === "negated"} aria-hidden="true">
    {#if route}
      <svg class="absolute inset-0 size-full overflow-visible" viewBox={`0 0 ${innerWidth} ${innerHeight}`} preserveAspectRatio="none">
        <defs>
          <marker id="attack-arrowhead" markerWidth="12" markerHeight="12" refX="8.5" refY="5" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L9,5 L0,10 L2.6,5 Z" fill="var(--color-gold)" />
          </marker>
          <filter id="attack-arc-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <path class="arc-halo" d={route.path} />
        <path class="arc-core" d={route.path} marker-end="url(#attack-arrowhead)" />
        <path class="arc-current" d={route.path} />
      </svg>
      <span class="attack-node attacker-node" style={`left:${route.start.x}px;top:${route.start.y}px;--node:${d.attacker.controller === duel.seat ? "var(--color-cyan)" : "var(--color-magenta)"}`}></span>
      <span class="attack-node defender-node" style={`left:${route.end.x}px;top:${route.end.y}px;--node:${d.defenderController === duel.seat ? "var(--color-cyan)" : "var(--color-magenta)"}`}></span>
    {/if}
  </div>

  <div class="attack-versus pointer-events-none fixed inset-x-0 top-[3.15rem] z-[48] mx-auto flex items-stretch" class:is-striking={d.phase === "striking"} class:is-negated={d.phase === "negated"} role="status" aria-live="polite" aria-label={`${status}: ${attackerOwner}'s ${attackerName} attacks ${d.target ? `${defenderOwner}'s ${defenderName}` : defenderOwner + ' directly'}`}>
    <div class="combatant combatant-attacker">
      {#if d.attackerCode}<img src={cardImage(d.attackerCode)} alt="" />{/if}
      <div class="min-w-0">
        <span class="role-label">Attacker · {attackerOwner}</span>
        <strong>{attackerName}</strong>
        <span class="power-label">ATK {fmt(d.attackerAtk)}</span>
      </div>
    </div>
    <div class="declaration-state">
      <span class="crossed-swords" aria-hidden="true">⚔</span>
      <span>{status}</span>
    </div>
    <div class="combatant combatant-defender">
      {#if d.targetCode}<img src={cardImage(d.targetCode)} alt="" />{:else}<span class="direct-reticle" aria-hidden="true"></span>{/if}
      <div class="min-w-0 text-right">
        <span class="role-label">Defender · {defenderOwner}</span>
        <strong>{defenderName}</strong>
        {#if d.target}<span class="power-label">{d.defenderInDefense ? "DEF" : "ATK"} {fmt(d.defenderPower)}</span>{:else}<span class="power-label">Life Points</span>{/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .attack-versus {
    --state: var(--color-gold);
    width: min(44rem, calc(100vw - 34rem));
    min-width: 28rem;
    filter: drop-shadow(0 10px 22px rgba(0, 0, 0, 0.58));
    animation: declaration-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .attack-versus.is-striking { --state: #ffffff; }
  .attack-versus.is-negated { --state: var(--color-magenta); }
  .combatant {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-width: 0;
    flex: 1 1 0;
    padding: 0.42rem 0.7rem;
    background: rgba(6, 4, 17, 0.96);
    border-block: 1px solid color-mix(in oklab, var(--state) 48%, transparent);
  }
  .combatant-attacker {
    border-left: 1px solid color-mix(in oklab, var(--color-gold) 72%, transparent);
    clip-path: polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px);
  }
  .combatant-defender {
    flex-direction: row-reverse;
    border-right: 1px solid color-mix(in oklab, var(--color-magenta) 72%, transparent);
    clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%);
  }
  .combatant img, .direct-reticle {
    width: 1.72rem;
    height: 2.48rem;
    flex: 0 0 auto;
    object-fit: cover;
    border-radius: 2px;
    box-shadow: 0 0 0 1px color-mix(in oklab, var(--state) 60%, transparent), 0 0 12px color-mix(in oklab, var(--state) 25%, transparent);
  }
  .role-label, .power-label, .combatant strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .role-label {
    color: var(--state);
    font: 700 0.52rem/1.05 Rajdhani, sans-serif;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
  .combatant strong {
    margin-top: 0.1rem;
    color: var(--color-ink);
    font: 700 0.72rem/1.05 Rajdhani, sans-serif;
  }
  .power-label {
    margin-top: 0.12rem;
    color: var(--color-ink-dim);
    font: 700 0.58rem/1 Rajdhani, sans-serif;
    letter-spacing: 0.08em;
  }
  .declaration-state {
    position: relative;
    z-index: 1;
    display: grid;
    width: 6.4rem;
    flex: 0 0 auto;
    place-items: center;
    align-content: center;
    gap: 0.08rem;
    color: var(--state);
    background: color-mix(in oklab, var(--color-void) 88%, var(--state));
    border: 1px solid color-mix(in oklab, var(--state) 72%, transparent);
    box-shadow: 0 0 18px color-mix(in oklab, var(--state) 18%, transparent), inset 0 0 14px color-mix(in oklab, var(--state) 8%, transparent);
    clip-path: polygon(9px 0, calc(100% - 9px) 0, 100% 50%, calc(100% - 9px) 100%, 9px 100%, 0 50%);
    font: 700 0.52rem/1 Rajdhani, sans-serif;
    letter-spacing: 0.09em;
    text-align: center;
    text-transform: uppercase;
  }
  .crossed-swords { font-size: 0.9rem; line-height: 1; filter: drop-shadow(0 0 5px currentColor); }
  .direct-reticle { position: relative; border-radius: 50%; }
  .direct-reticle::before, .direct-reticle::after { content: ""; position: absolute; inset: 26%; border: 1px solid var(--state); border-radius: 50%; }
  .direct-reticle::after { inset: 46% -12%; border-width: 1px 0 0; border-radius: 0; }

  .arc-halo, .arc-core, .arc-current { fill: none; vector-effect: non-scaling-stroke; }
  .arc-halo { stroke: var(--color-gold); stroke-width: 13; opacity: 0.14; filter: url(#attack-arc-glow); }
  .arc-core { stroke: color-mix(in oklab, var(--color-gold) 80%, white); stroke-width: 2.5; opacity: 0.92; }
  .arc-current {
    stroke: white;
    stroke-width: 1.25;
    stroke-dasharray: 10 24;
    stroke-linecap: round;
    opacity: 0.85;
    animation: current-flow 620ms linear infinite;
  }
  .is-striking .arc-halo { opacity: 0.32; stroke-width: 18; }
  .is-striking .arc-core { stroke: white; stroke-width: 4; }
  .is-negated .arc-halo, .is-negated .arc-core, .is-negated .arc-current { stroke: var(--color-magenta); }
  .is-negated .arc-current { animation: none; opacity: 0.35; }
  .attack-node {
    position: absolute;
    width: 2.1rem;
    aspect-ratio: 1;
    translate: -50% -50%;
    border: 2px solid var(--node);
    border-radius: 50%;
    box-shadow: 0 0 0 4px color-mix(in oklab, var(--node) 14%, transparent), 0 0 18px var(--node);
    animation: node-pulse 1.05s cubic-bezier(0.22, 1, 0.36, 1) infinite;
  }
  .defender-node::before, .defender-node::after { content: ""; position: absolute; background: var(--node); }
  .defender-node::before { left: 50%; top: -0.42rem; width: 1px; height: calc(100% + 0.84rem); }
  .defender-node::after { top: 50%; left: -0.42rem; width: calc(100% + 0.84rem); height: 1px; }

  /* `inset-inline:0` + auto margins own horizontal centring. The entrance can safely animate
     transform without composing with any translateX utility — including on stale/variant Tailwind
     output where translate utilities are implemented through the transform property. */
  @keyframes declaration-in { from { opacity: 0; transform: translateY(-8px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
  @keyframes current-flow { to { stroke-dashoffset: -34; } }
  @keyframes node-pulse { 50% { scale: 1.08; opacity: 0.74; } }

  @media (max-width: 900px) {
    .attack-versus { top: 2.65rem; width: min(35rem, 70vw); min-width: 24rem; }
    .combatant { padding: 0.32rem 0.48rem; }
    .combatant img, .direct-reticle { width: 1.4rem; height: 2rem; }
    .declaration-state { width: 5.3rem; }
  }
  @media (prefers-reduced-motion: reduce) {
    .attack-versus { animation: none; }
    .arc-current, .attack-node { animation: none; }
  }
</style>
